import { createFileRoute } from "@tanstack/react-router";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import "./newOrder.css";
import { useSellOrderMutation } from "@/features/order/api";
import { formDataOrderMapper } from "@/features/order/mapper";
import { CreateInvoiceForm } from "@/features/order/types";
import { toast } from "react-toastify";
import { decimal, DecimalCalc } from "@/utils";

const invoiceItemSchema = z.object({
  productName: z.string().min(1, "Product name is required"),
  quantity: z.number().min(1, "Quantity must be at least 1"),
  price: z.number().min(0, "Price cannot be negative"),
  unit: z.string().min(1, "Unit is required"),
});

const invoiceFormSchema = z.object({
  customerName: z.string().min(1, "Customer name is required"),
  customerPhoneNumber: z
    .string()
    .min(10, "Phone number must be at least 10 digits")
    .max(15, "Phone number cannot exceed 15 digits")
    .regex(
      /^\d{10,15}$/,
      "Phone number must contain only numbers (10-15 digits)"
    ),
  address: z.string().min(1, "Address is required"),
  discount: z.number().min(0, "Discount cannot be negative"),
  discountType: z.enum(["%", "Fixed"]),
  notes: z.string().optional(),
  invoiceNumber: z.string().min(1, "Invoice number is required"),
  invoiceDate: z.date({
    required_error: "Invoice date is required",
    invalid_type_error: "Invalid date format",
  }),

  paymentMethod: z.enum(["Cash", "UPI", "Card", "Credit"]),
  itemList: z
    .array(invoiceItemSchema)
    .min(1, "At least one item is required")
    .refine((items) => items.every((item) => item.price * item.quantity > 0), {
      message: "All items must have a positive total amount",
    }),
  // totalAmount: z.number().min(0.01, "Total amount must be positive"),
});

const initialItemState = {
  productName: "",
  quantity: 1,
  price: 0.0,
};

const initialState = {
  customerName: "",
  customerPhoneNumber: "",
  address: "",
  discount: 0.0,
  discountType: "%" as const, // This matches the enum in your schema
  notes: "",
  invoiceNumber: "",
  invoiceDate: new Date().toISOString().substring(0, 10),
  paymentMethod: "Cash" as const,
  itemList: [initialItemState],
  // totalAmount: 0,
};

export const Route = createFileRoute("/(app)/(store)/order/newOrder/")({
  component: RouteComponent,
});

function RouteComponent() {
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...initialState,
      invoiceNumber: getCurrentInvoiceNumber(),
    },
    resolver: zodResolver(invoiceFormSchema),
  });

  const { fields, remove, insert } = useFieldArray({
    control,
    name: "itemList",
  });

  const [createOrder , {isError}] = useSellOrderMutation();

  async function onSubmit(params: CreateInvoiceForm) {
    try {
      const payload = formDataOrderMapper(params);
      const response = await createOrder(payload).unwrap();

      alert("Invoice saved successfully!");
    } catch (error:any) {
       toast.error(error?.data.message??"Something went wrong.")
    }
  }

  const itemList = watch("itemList");
  const subtotal = DecimalCalc.sum(
    itemList?.map(item => 
      decimal(item.price || 0).mul(item.quantity || 0)
    ) || []
  ).toNumber();
  const isPercentage = watch("discountType") === "%";
  let discountAmount = watch("discount");
  if (isPercentage) {
    discountAmount = decimal(subtotal).mul(discountAmount).div(100).toNumber();
  } else {
    discountAmount = DecimalCalc.min([decimal(discountAmount), decimal(subtotal)]).toNumber();
  }

  function getCurrentInvoiceNumber() {
    const today = new Date();
    const year = today.getFullYear().toString();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    const counter = localStorage.getItem("invoiceCounter") || "1";
    const paddedCounter = counter.padStart(3, "0");

    return `INV-${year}${month}${day}${paddedCounter}`;
  }

  async function saveInvoiceToDraft(data: any) {
    try {
      let list = localStorage.getItem("invoiceList");
      const newList = list ? await JSON.parse(list) : [];
      localStorage.setItem("invoiceList", JSON.stringify([...newList, data]));
    } catch (error:any) {
       toast.error(error?.data.message??"Something went wrong.")
    }
  }

  function handleRemoveItem(index: any) {
    if (itemList.length == 1) {
      alert("At list One element is required for billing");
    } else {
      remove(index);
    }
  }

  return (
    <div>
      <nav className="navBar">
        <div className="navContainer">
          <div className="navContent">
            <div className="navFlex">
              <div className="logoContainer">
                {/* Replace with your logo component */}
                <div>LOGO</div>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <div className="pageContainer">
        <form id="myForm">
          <main className="mainContent">
            <div className="contentWrapper">
              <div className="flexWrapper">
                {/* Left Column */}
                <div className="leftColumn">
                  <div className="customerInfoCard">
                    <h2 className="sectionTitle">Customer Information</h2>
                    <div className="gridLayout">
                      <div>
                        <label className="inputLabel">Company Name</label>
                        <input
                          {...register("customerName")}
                          type="text"
                          className="textInput"
                          placeholder="Enter customer name"
                        />
                        <ErrorMessage error={errors.customerName} />
                      </div>

                      <div>
                        <label className="inputLabel">Phone Number</label>
                        <input
                          type="tel"
                          {...register("customerPhoneNumber", {
                            setValueAs: (v) => v.replace(/\D/g, ""),
                          })}
                          className="textInput"
                          placeholder="Enter phone number"
                        />
                        <ErrorMessage error={errors.customerPhoneNumber} />
                      </div>

                      <div className="fullWidthField">
                        <label className="inputLabel">Billing Address</label>
                        <textarea
                          className="textArea"
                          rows={3}
                          {...register("address")}
                          placeholder="Enter billing address"
                        ></textarea>
                        <ErrorMessage error={errors.address} />
                      </div>
                    </div>
                  </div>

                  <div className="invoiceItemsCard">
                    <h2 className="sectionTitle">Invoice Items</h2>

                    <div className="tableContainer">
                      <table className="itemsTable">
                        <thead>
                          <tr className="tableHeaderRow">
                            <th className="serialNumberHeader">S.R.</th>
                            <th className="productNameHeader">Product Name</th>
                            <th className="quantityHeader">Quantity</th>
                            <th className="unitHeader">Unit</th>
                            <th className="priceHeader">Unit Price</th>
                            <th className="amountHeader">Amount</th>
                            <th className="actionHeader"></th>
                          </tr>
                        </thead>
                        <tbody id="itemRows" className="tableBody">
                          {fields.map((field, index) => {
                            const currentItem = itemList[index] || {};
                            const total = decimal(currentItem.price || 0)
                              .mul(currentItem.quantity || 0)
                              .toNumber();

                            return (
                              <tr key={field.id} className="itemRow">
                                <td className="serialNumberCell">
                                  <span>{index + 1}.</span>
                                </td>
                                <td className="productNameCell">
                                  <input
                                    {...register(
                                      `itemList.${index}.productName`
                                    )}
                                    type="text"
                                    className="itemInput"
                                    placeholder="Item Description"
                                    required
                                  />
                                  <ErrorMessage
                                    error={
                                      errors?.itemList?.[index]?.productName
                                    }
                                  />
                                </td>
                                <td className="quantityCell">
                                  <input
                                    {...register(`itemList.${index}.quantity`, {
                                      valueAsNumber: true,
                                    })}
                                    type="number"
                                    className="itemInput numberInput"
                                    required
                                  />
                                  <ErrorMessage
                                    error={errors?.itemList?.[index]?.quantity}
                                  />
                                </td>
                                <td className="unitCell">
                                  <select
                                    className="itemSelect"
                                    {...register(`itemList.${index}.unit`)}
                                    defaultValue={"KG"}
                                  >
                                    <option value="KG">KG</option>
                                    <option value="G">Gram</option>
                                    <option value="Piece">Piece</option>
                                    <option value="Ltr">Ltr</option>
                                    <option value="Ltr">ml</option>
                                  </select>
                                </td>
                                <td className="priceCell">
                                  <input
                                    {...register(`itemList.${index}.price`, {
                                      valueAsNumber: true,
                                    })}
                                    type="number"
                                    className="itemInput numberInput"
                                    placeholder="0.00"
                                    required
                                  />
                                  <ErrorMessage
                                    error={errors?.itemList?.[index]?.price}
                                  />
                                </td>
                                <td className="totalCell">
                                  ₹{total.toFixed(2)}
                                </td>
                                <td className="actionCell">
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveItem(index)}
                                    className="removeButton"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="addItemContainer">
                      <button
                        id="addRow"
                        type="button"
                        onClick={() =>
                          insert(fields.length, {
                            productName: "",
                            quantity: 1,
                            price: 0.0,
                            unit: "KG",
                          })
                        }
                        className="addItemButton"
                      >
                        <i className="fas fa-plus mr-2"></i>Add Item
                      </button>
                    </div>
                  </div>

                  <div className="additionalInfoCard">
                    <h2 className="sectionTitle">Additional Information</h2>
                    <div className="notesContainer">
                      <div>
                        <label className="inputLabel">Notes</label>
                        <textarea
                          className="textArea"
                          rows={4}
                          {...register(`notes`)}
                          placeholder="Enter additional notes"
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Right Column */}
                <div className="rightColumn">
                  <div className="invoiceDetailsCard">
                    <h2 className="sectionTitle">Invoice Details</h2>
                    <div className="detailsContainer">
                      <div>
                        <label className="inputLabel">Invoice Number</label>
                        <input
                          id="invoiceDisplay"
                          {...register(`invoiceNumber`)}
                          type="text"
                          className="textInput"
                          value={getCurrentInvoiceNumber()}
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="inputLabel">Invoice Date</label>
                        <input
                          id="dateInput"
                          {...register("invoiceDate", {
                            valueAsDate: true,
                            required: "Invoice date is required",
                          })}
                          type="date"
                          className="textInput"
                        />
                      </div>

                      <div>
                        <label className="inputLabel">Payment Method</label>
                        <select
                          {...register(`paymentMethod`)}
                          defaultValue={"Cash"}
                          className="textInput"
                        >
                          <option>UPI</option>
                          <option>Card</option>
                          <option>Credit</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="summaryCard">
                    <h2 className="sectionTitle">Summary</h2>
                    <div className="summaryContainer">
                      <div className="summaryRow">
                        <span className="summaryLabel">Subtotal</span>
                        <span className="summaryValue" id="subtotal">
                          ₹{subtotal.toFixed(2)}
                        </span>
                      </div>

                      <div className="discountContainer">
                        <label className="inputLabel">Discount</label>
                        <div className="discountInputGroup">
                          <input
                            id="discount"
                            {...register(`discount`, { valueAsNumber: true })}
                            type="number"
                            className="discountInput"
                            placeholder="0"
                          />
                          <select
                            id="discountType"
                            {...register(`discountType`)}
                            className="discountSelect"
                          >
                            <option>%</option>
                            <option>Fixed</option>
                          </select>
                        </div>
                      </div>

                      <div className="totalContainer">
                        <div className="totalRow">
                          <span className="totalLabel"></span>
                          <span className="totalAmount" id="totalAmount">
                            ₹{(subtotal - discountAmount).toFixed(2)}
                          </span>
                        </div>
                        <input
                          type="hidden"
                          {...register("totalAmount", {
                            value: subtotal - discountAmount,
                          })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="buttonGroup">
                    <button
                      className="draftButton"
                      type="button"
                      onClick={handleSubmit(saveInvoiceToDraft)}
                    >
                      Save Draft
                    </button>
                    <button
                      className="printButton"
                      onClick={handleSubmit(onSubmit)}
                    >
                      Print Invoice
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </form>
      </div>
    </div>
  );
}
function ErrorMessage({ error }: { error?: { message?: string } }) {
  if (!error?.message) return null;
  return <p className="errorMessage">{error.message}</p>;
}
